import { Fragment, type ReactNode } from 'react';
import { Kbd } from '@/components/ui/kbd';
import { cn } from '@/lib/utils';

const isApple = () =>
  typeof navigator !== 'undefined' && /mac|iphone|ipad/i.test(navigator.platform || navigator.userAgent);

const SYMBOLS: Record<string, string> = {
  shift: '⇧',
  alt: '⌥',
  enter: '↵',
  esc: 'Esc',
  escape: 'Esc',
  up: '↑',
  down: '↓',
  left: '←',
  right: '→',
  space: '␣',
};

/** `mod` renders as ⌘ on Apple hardware and Ctrl everywhere else. */
export function formatKey(key: string): string {
  const lower = key.toLowerCase();
  if (lower === 'mod') return isApple() ? '⌘' : 'Ctrl';
  return SYMBOLS[lower] ?? (key.length === 1 ? key.toUpperCase() : key);
}

export interface KbdHintProps {
  /** `'mod+k'`, `'g l'` for a sequence, or an explicit list of chips. */
  keys: string | readonly string[];
  label?: ReactNode;
  className?: string;
}

function toChips(keys: string | readonly string[]): string[][] {
  if (Array.isArray(keys)) return (keys as readonly string[]).map((k) => [k]);
  return String(keys)
    .split(' ')
    .filter(Boolean)
    .map((chord) => chord.split('+'));
}

/** The muted keyboard hint that trails a menu item, tooltip or empty state. */
export function KbdHint({ keys, label, className }: KbdHintProps) {
  const chords = toChips(keys);
  const spoken = chords.map((chord) => chord.map(formatKey).join(' ')).join(', luego ');

  return (
    <span className={cn('inline-flex items-center gap-1.5 text-xs text-muted-foreground', className)}>
      {label && <span>{label}</span>}
      <span className="inline-flex items-center gap-1" aria-label={`Atajo: ${spoken}`}>
        {chords.map((chord, index) => (
          <Fragment key={index}>
            {index > 0 && <span aria-hidden>·</span>}
            {chord.map((key) => (
              <Kbd key={key}>{formatKey(key)}</Kbd>
            ))}
          </Fragment>
        ))}
      </span>
    </span>
  );
}
